import { networkIconFor } from '@alison/ui';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';
import { formatCount } from '../../lib/format.js';
import { CARD_SX, type GemPicks, ONE_LINE, UPLIFT_SX } from './GemCard.js';
import { CHECKBOX_SX, Pill, PRIMARY_PILL_SX } from './Pill.js';
import type { RisingElement, RisingGroup } from './risingRows.js';

function RisingElementCard({
  element,
  picks,
}: {
  element: RisingElement;
  picks?: GemPicks;
}): ReactNode {
  const NetworkIcon = networkIconFor(element.network);
  const count = element.creativeCount;

  return (
    <Stack data-testid={`rising-${element.key}`} sx={CARD_SX}>
      <Stack direction="row" sx={{ gap: 1.5, alignItems: 'flex-start' }}>
        {picks === undefined ? null : (
          <Checkbox
            size="small"
            checked={picks.picked.includes(element.key)}
            onChange={() => picks.onToggle(element.key)}
            sx={{ ...CHECKBOX_SX, mt: 0.25 }}
            slotProps={{ input: { 'aria-label': `${element.feature}: ${element.tag}` } }}
          />
        )}
        <Stack sx={{ gap: 0.5, flex: 1, minWidth: 0 }}>
          <Tooltip title={element.tag}>
            <Typography sx={{ fontSize: 13, fontWeight: 600, ...ONE_LINE }}>
              {element.tag}
            </Typography>
          </Tooltip>
          <Stack direction="row" sx={{ gap: 0.75, alignItems: 'center', minWidth: 0 }}>
            {NetworkIcon === undefined ? null : (
              <NetworkIcon
                titleAccess={element.network ?? undefined}
                sx={{ fontSize: 14, flexShrink: 0 }}
              />
            )}
            <Typography sx={{ fontSize: 12, color: 'text.secondary', ...ONE_LINE }}>
              {element.feature} · {count} creative{count === 1 ? '' : 's'}
            </Typography>
          </Stack>
        </Stack>
        {element.measureLabel === null ? null : (
          <Pill sx={PRIMARY_PILL_SX}>{element.measureLabel.toUpperCase()}</Pill>
        )}
      </Stack>

      <Stack direction="row" sx={{ gap: 1, alignItems: 'baseline', flexWrap: 'wrap' }}>
        <Typography sx={{ fontSize: 13, fontWeight: 600 }} data-testid="rising-values">
          {element.previousValue === null ? null : <>{formatCount(element.previousValue)} → </>}
          {formatCount(element.currentValue)}
        </Typography>
        {element.changePercent === null ? null : (
          <Pill sx={UPLIFT_SX}>↑ {Math.round(element.changePercent)}%</Pill>
        )}
      </Stack>
    </Stack>
  );
}

export function RisingElementsView({
  groups,
  picks,
}: {
  groups: RisingGroup[];
  picks?: GemPicks;
}): ReactNode {
  return (
    <Stack sx={{ gap: 4 }} data-testid="rising-groups">
      {groups.map((group) => (
        <Stack key={group.category} sx={{ gap: 2 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 600, px: 1 }}>{group.category}</Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
              gap: 2,
            }}
          >
            {group.elements.map((element) => (
              <RisingElementCard
                key={element.key}
                element={element}
                {...(picks === undefined ? {} : { picks })}
              />
            ))}
          </Box>
        </Stack>
      ))}
    </Stack>
  );
}
